import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CONFIRMATION_MODE, CONFIRMATION_RESULT } from '@wizeoo/akm-core-lib';
import {
  BATCH_FILE_FORMAT,
  ConfirmationResultObj,
  FilterComponent,
  WizDatasource,
  WizGroupCommand,
  WizIcon,
  WizPopUp,
  WizRoot,
  WizEventManager, WizEvent,
} from '@wizeoo/akm-component-lib';
import { TimegapCmd } from '@wizeoo/akm-common-lib';
import { MultiDisplay, MultiDisplayConfig } from '@wizeoo/akm-common-lib';
import { PermissionAccess } from '@wizeoo/akm-common-lib';
import { AlkEvent } from '@wizeoo/akm-common-lib';
import { BATCH_KIND } from '@wizeoo/akm-common-lib';
import { ALK_EVENT_TYPE } from '@wizeoo/akm-common-lib';
import { BaseView } from '@wizeoo/akm-common-lib';
import { ChronocodeCmd } from '@wizeoo/akm-common-lib';
import { PermissionVisibility, StaticFeatureEnum } from '@wizeoo/akm-common-lib';
import { AgLabelValidityRenderer } from '@wizeoo/akm-common-lib';
import { UserContextService } from '@wizeoo/akm-common-lib';
import { Model } from './../../model/model';
import { ViewData } from '@wizeoo/akm-common-lib';
import { ChronocodeVO } from '@wizeoo/akm-common-lib';
import { ChronocodeSmallRenderer } from './renderer/main/chronocode-small.renderer';
import { MdChronoCodeTileRenderer } from './renderer/main/md-chronocode-tile.renderer';
import { RouteEngine } from 'src/app/manager/routing/route.engine';

/**
 * Vue Principale des chronocodes
 * Author : SVA
 */
@Component({
  selector: 'chronocode-main-view',
  templateUrl: './chronocode-main.view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChronocodeMainView extends BaseView implements OnDestroy {
  @ViewChild('multiDisp', { read: MultiDisplay, static: false }) multiDisp: MultiDisplay<ChronocodeVO>;
  /** Bouton de céation d'arbre de chronocodes */
  @ViewChild('btCreateChronocode', { read: WizIcon, static: false }) btCreateChronocode: WizIcon;
  /** Bouton d'export */
  @ViewChild('btnExport', { read: WizIcon, static: false }) btnExport: WizIcon;
  /** Bouton d'import */
  @ViewChild('btnImport', { read: WizIcon, static: false }) btnImport: WizIcon;
  /** Bouton de suppression d'un chronocode */
  @ViewChild('btnRemove', { read: WizIcon }) btnRemove: WizIcon;

  /** Datasource des chronocodes' */
  public chronocodeDS: WizDatasource<ChronocodeVO> = new WizDatasource<ChronocodeVO>([])
    .setSortFields([{ property: 'startDate', way: 'asc', numeric: true }])
    .setSelectionMode('single')
    .build();

  public multiDisplayConfig: MultiDisplayConfig<ChronocodeVO> = {
    model: Model.getInstance(),
    datasource: this.chronocodeDS,
    defaultMode: 'tiles',
    onSelectionChanging: this.askSelectFromList.bind(this),
    tiles: {
      enabled: true,
      renderer: MdChronoCodeTileRenderer,
      itemHeight: 150,
      itemWidth: 200,
    },
    grid: {
      enabled: true,
      columns: [
        {
          headerName: '$.common.code.$',
          field: 'code',
          width: 80,
          sortable: true,
        },
        {
          headerName: '$.common.label.$',
          field: 'trsLabel',
          flex: 1,
          cellRenderer: 'labelValidityRenderer',
          sortable: true,
        },
      ],
      renderers: {
        labelValidityRenderer: AgLabelValidityRenderer,
      },
    },
    filter: {
      enabled: true,
      initFilter: this.initFilter.bind(this),
    },
  };

  /** Référence au composant de filtre présent dans le composant multi display */
  private mainFilter: FilterComponent;

  private initFilter(pFilter: FilterComponent): FilterComponent {
    this.mainFilter = pFilter;
    return this.mainFilter;
  }

  constructor(elementRef: ElementRef) {
    super(elementRef);
  }

  private get _context(): UserContextService {
    return Model.getInstance().userContextService;
  }

  /* ################################################
                      cycle de vie
   ################################################ */

  /** chargement des données indispensables */
  public wizBeforeInit(): WizGroupCommand {
    super.wizBeforeInit();
    const calls = [];
    if (!Model.getInstance().timegapList) {
      // chargement des intervalles de temps
      calls.push(new TimegapCmd(Model.getInstance()).find());
    }
    calls.push(new ChronocodeCmd(Model.getInstance()).find());
    return new WizGroupCommand().configure(calls);
  }

  /** Alimentation des données */
  public wizSynchronizeData(): void {
    super.wizSynchronizeData();

    super.managePermissions([
      {
        feature: StaticFeatureEnum.CHRONOCODE,
        permissions: [
          {
            component: this.btCreateChronocode,
            accesses: [PermissionAccess.CREATE],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnRemove,
            accesses: [PermissionAccess.DELETE],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnExport,
            accesses: [PermissionAccess.BATCH],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnImport,
            accesses: [PermissionAccess.BATCH],
            visibility: PermissionVisibility.DISABLED,
          },
        ],
      },
    ]);

    // flagger les chronocodes utilisés dans les visions (pas supprimables)
    Model.getInstance().chronocodeEngine.checkUsedChronocodes(Model.getInstance().chronocodeTreeList);
    this.chronocodeDS.setSource(Model.getInstance().chronocodeTreeList);

    const defaultChronoCode: ChronocodeVO = Model.getInstance().chronocodeEngine.getDefaulChronocode(); // sélection
    this.chronocodeDS.select([defaultChronoCode]);

    this.buildFilter();
    this.feed(defaultChronoCode, false);
  }

  public wizAddListener(): void {
    super.wizAddListener();
    // Demande de duplication d'un chronocode
    WizEventManager.addListener(this, this.elementRef, ALK_EVENT_TYPE.DUPLICATE_DATA, this.askToDuplicateChronoCode);
    // Création d'arbre de chronocodes
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.CHRONO_CREATE, this.creationHandler);
    // Demande de rafrachissement suite à modification d'un élément de premier niveau
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.CHRONO_LABEL_CHANGE, this.itemUpdated);

    // écoute du retour d'import (batch pour refresh de la vue)
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.BATCH_IMPORT_RETURN, this.backFromImport);
  }

  public wizRemoveListener(): void {
    super.wizRemoveListener();
    WizEventManager.removeListener(this, this.elementRef, ALK_EVENT_TYPE.DUPLICATE_DATA);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.CHRONO_CREATE);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.CHRONO_LABEL_CHANGE);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.BATCH_IMPORT_RETURN);
  }

  /** accès interdit aux fonctions import / export */
  public noChrono(): boolean {
    return false; // TODO, restriction par chrono. Les batchs renvoient tout.. TODO check BU
  }

  public doAct(action: string): void {
    const hasPendingChanges = ViewData.wizUpdateModel;
    switch (action) {
      case 'export':
        if (!!Model.getInstance().cloneChronocode) {
          Model.getInstance().batchEngine.export(BATCH_KIND.CHRONOCODE, BATCH_FILE_FORMAT.CSV, Model.getInstance().cloneChronocode.code);
        } else {
          Model.getInstance().batchEngine.export(BATCH_KIND.CHRONOCODE);
        }
        break;
      case 'import':
        Model.getInstance().batchEngine.import(BATCH_KIND.CHRONOCODE);
        break;
      case 'create':
        this.askForCreate();
        break;
      case 'delete':
        this.askForRemove();
        break;
    }
  }

  /** mémorisation du filtre */
  public wizOnDestroy(): void {
    super.wizOnDestroy();
    // this._context.saveLocalFilter(this.mainFilter);
  }

  /**
   * construction de la zone de filtre des chronocodes
   * @param event : Evenement de changement dans le text input de filtre
   */
  private buildFilter(): void {
    this.mainFilter.configure(['code', 'trsLabel'], null, null);
    // le contextEngine prend le relais pour alimenter les filtres par défaut
    this._context.feedFilterContext(this.mainFilter);
  }

  /** alimentation de la zone détail, ou pas */
  public feed(chrono: ChronocodeVO, updateContext: boolean = true): void {
    
    const clone = Model.getInstance().cloneChronocode; // le clone en cours d'édition

    if (!chrono || (clone && chrono && clone.code === chrono.code)) { // pas de sélection, ou c'est moi
      // déselection
      Model.getInstance().chronocodeEngine.setCurrentEdited(null);
      this.navigateToSubview(RouteEngine.CHRONO_HOME_VIEW); // retour vers la vue "Main" avec les templates
    } else {
      Model.getInstance().chronocodeEngine.setCurrentEdited(chrono);
      this.navigateToSubview(RouteEngine.CHRONO_ELT_VIEW); // direction vue de détail
    }

    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, false)); // reset du modelUpdate

    this.manageComponents();
  }

  /** activation ou pas des fonctions d'interaction et choix de l'affichage du détail */
  public manageComponents(): void {
    this.btnRemove.wizDisabled =
      !Model.getInstance().cloneChronocode ||
      Model.getInstance().cloneChronocode.isUsed ||
      !Model.getInstance().permissionEngine.canDelete(StaticFeatureEnum.CHRONOCODE);
  }

  /* ################################################
                    interactions
   ################################################ */

  /** demande de changement de sélection */
  private askSelectFromList(e: WizEvent = null): void {
    const vo: ChronocodeVO = e.value;
    if (ViewData.wizUpdateModel) {
      // modifs en cours
      if (this.chronocodeDS.isSelected(vo)) {
        e.preventDefault();
        return;
      }
      e.preventDefault();

      WizPopUp.confirm(
        '$.common.pending.update.title.$',
        '$.common.pending.update.detail.$',
        this.confirmSwap.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        vo,
        null,
        null
      ).show();
    } else {
      // sinon sélection / déselection
      this.feed(vo);
    }
  }

  /**
   * Confirmation de la sélection du chronocode, on met à jour la datasource puis la vue
   * On ne peut pas passer directement par le doSelectFromTree car la méthode est utilisée aussi
   * dans le cas ou on ne prevent pas la sélection. On ferait le toggleSelect deux fois sinon
   */
  private confirmSwap(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      const chronocode: ChronocodeVO = result.data;
      this.feed(chronocode);
      this.chronocodeDS.toggle(chronocode); // et toggle de la liste
    }
  }

  /**
   * Demande de création d'un chronocode
   * @param event Clic bouton de création
   */
  private askForCreate(): void {
    if (ViewData.wizUpdateModel) {
      WizPopUp.confirm(
        '$.common.pending.update.title.$',
        '$.common.pending.update.detail.$',
        this.doCreate.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        Model.getInstance().currentChronocode,
        null,
        null
      ).show();
    } else {
      this.doCreate(ConfirmationResultObj.build(CONFIRMATION_RESULT.CONFIRM, null));
    }
  }

  /**
   * Création d'arbre de chronocodes
   * @param event Clic bouton de création
   */
  private doCreate(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      Model.getInstance().chronocodeEngine.addChronocode(); // va créer une nouvelle instance vide, et gérer le currentEdited et le clone
      this.chronocodeDS.clearSelection();
      Model.getInstance().chronocodeEngine.setCurrentEdited(null);
      this.navigateToSubview(RouteEngine.CHRONO_CREATION_VIEW); // vers la vue de création
    }
  }

  /**
   * Demande de suppression depuis le multi display
   * @param e Evenement de suppression
   */
  private askForRemove(): void {
    const toRemove: ChronocodeVO = Model.getInstance().currentChronocode;
    // si des changement sont en cours
    if (ViewData.wizUpdateModel) {
      // ici on demande une confirmation d'abandont des modificaitons non sauvegardées en cours
      WizPopUp.confirm(
        '$.common.pending.update.title.$',
        '$.common.pending.update.detail.$',
        this.doRemove.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        toRemove,
        null,
        null
      ).show();
    } else {
      this.doRemove(ConfirmationResultObj.build(CONFIRMATION_RESULT.CONFIRM, toRemove));
    }
  }

  /** Suppression du chronoCode selectionné  */
  private doRemove(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      const toRemove: ChronocodeVO = result.data;
      // demande de confirmation de la suppression
      WizPopUp.confirm(
        '$.common.delete.title.$',
        '$.common.delete.detail.$',
        this.reallyRemove.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        toRemove,
        ChronocodeSmallRenderer
      ).show();
    }
  }

  /**
   * Confirmation de la demande de suppression
   * @param result, confirmation ou non de la suppression
   */
  private reallyRemove(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      const toRemove: ChronocodeVO = result.data;
      // appel de service
      Model.getInstance().chronocodeEngine.remove(toRemove, this.backFromRemove.bind(this));
    }
  }

  /**
   * Retour de service de suppression
   * @param bddBack Chronocode supprimé
   */
  private backFromRemove(bddBack: ChronocodeVO): void {
    this.chronocodeDS.setSource(Model.getInstance().chronocodeTreeList);
    Model.getInstance().navigationEngine.navigate(RouteEngine.CHRONO_VIEW); // retour en vue "Main"
    this.feed(null);
  }

  /** Navigation vers une sous-vue ou mise à jour de celle-ci */
  private navigateToSubview(pView: string): void {
    if (pView) {
      const currentView: BaseView = Model.getInstance().navigationEngine.getCurrentSubView();
      if (currentView && currentView.code === pView) {
        // navigation self, cycle de refresh
        currentView.wizSynchronizeData();
      } else {
        // sinon navigation vraie
        Model.getInstance().navigationEngine.navigate(pView);
      }
    }
  }

  /* ################################################
             interactions des sous-vues
   ################################################ */

  /**
   * Handler de création d'arbre de chronocode : affichage de celui-ci
   * @param e Evenement de création émis par la sous-vue de création
   */
  private creationHandler(e: AlkEvent): void {
    this.chronocodeDS.setSource(Model.getInstance().chronocodeTreeList); // force le refresh de la liste
    const createdChronocode: ChronocodeVO = Model.getInstance().chronocodeEngine.getChronocode((e.data as ChronocodeVO).code);
    this.chronocodeDS.select([createdChronocode]);
    this.feed(createdChronocode);
  }

  /** Demande de duplication d'un chronocode
   * @param event Evénement de clic sur le bouton de duplication d'un chronocode
   */
  private askToDuplicateChronoCode(event: AlkEvent): void {
    const chronoCode: ChronocodeVO = event.data;
    WizPopUp.toast('$.chronocode.duplicate.$' + chronoCode.code, WizIcon.TOAST_SUCCESS).show();
  }

  /**
   * Rafraichissement de l'élément
   * @param event Evénement de modification du libellé de l'élément
   */
  private itemUpdated(event: AlkEvent): void {
    this.chronocodeDS.itemUpdated([event.data]);
    this.chronocodeDS.refresh();
  }

  /** callback sur retour d'import : recharger les Freedatas */
  private backFromImport(e: AlkEvent): void {
    // console.log('backFromImport', e);
    if (e && e.data === BATCH_KIND.CHRONOCODE) {
      new ChronocodeCmd(Model.getInstance()).find(this.backFromReload.bind(this)).execute();
    }
  }

  /** fredatas rechargés, refresh de la vue */
  private backFromReload(e: any): void {
    // console.log('backFromReload', e);
    this.wizSynchronizeData();
  }
}
